import * as React from "react";
import { Check, ChevronDown, X } from "../icons";
import { cn } from "../utils";
import { Button } from "../button";
import { Badge } from "../badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../command";
import { useApi } from "../api/client";
import { teamManage, type TeamMember } from "../api/contracts/team-manage";
import { auth } from "../api/contracts/auth";
// RO-3-02 / RO-4-04: additive subpath, not the bare barrel (see `path-label.ts`
// for the fuller note on why). `person-urn.ts` has zero imports of its own.
import { SOLO_SELF } from "@excellent/core/person-urn";

/**
 * The one team-member selector. Draws from the real workspace member list
 * (`/api/team/members`) — never free text — and stores each pick as a
 * `user:<id>` URN. Single mode backs the `urn-person` brief field; multi mode
 * backs `urn-people`. The current user is flagged "(You)" so "who owns X? — me"
 * resolves to an actual identity instead of the literal string "Me".
 *
 * Value contract:
 *   • single → a `user:<id>` URN string (or "" when unset)
 *   • multi  → an array of `user:<id>` URN strings
 * onChange always emits in that shape; onCommit (optional) fires after a change
 * settles so callers that debounce/save-on-blur can persist.
 */

const URN_PREFIX = "user:";

/**
 * Sentinel stored when the user is solo — single-player workspaces have no
 * registered members to point at, so "who owns this? — me" records this rather
 * than faking a member identity. Not a resolvable URN (no numeric id).
 *
 * The literal lives in `@excellent/core` (`person-urn.ts`) — the single source
 * of truth shared with the server-side writer (excellent/src/play/profile.ts).
 * Re-exported here so `@excellent/ui` consumers keep importing it unchanged.
 */
export { SOLO_SELF };

export function userUrn(userId: number): string {
  return `${URN_PREFIX}${userId}`;
}
export function userIdFromUrn(urn: string): number | null {
  if (!urn.startsWith(URN_PREFIX)) return null;
  const id = Number(urn.slice(URN_PREFIX.length));
  return Number.isInteger(id) ? id : null;
}

function toList(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === "string" && v.length > 0);
  if (typeof value === "string" && value.length > 0) return [value];
  return [];
}

export interface PersonPickerProps {
  /** `user:<id>` URN (single) or array of them (multi). */
  value: unknown;
  /** Single → emits a URN string (or ""); multi → emits a URN string[]. */
  onChange: (value: string | string[]) => void;
  /** Fired after a selection settles, for save-on-commit callers. */
  onCommit?: () => void;
  /** Allow more than one member. Default false. */
  multi?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** Accessible label for the trigger. */
  ariaLabel?: string;
  autoFocus?: boolean;
  /**
   * Extra classes for the portaled `PopoverContent`. The dropdown portals to
   * `document.body`, so a caller rendering inside a locally theme-scoped surface
   * (e.g. the Play "Screening Room" takeover, which scopes `.dark` on its root)
   * passes `"dark"` here to re-assert the theme on the portaled menu — CSS vars
   * cascade by DOM position, not React tree, so without it the menu resolves the
   * page-level tokens and mis-themes (a light menu over the dark room).
   */
  popoverClassName?: string;
}

export function PersonPicker({
  value,
  onChange,
  onCommit,
  multi = false,
  disabled,
  placeholder = "Select a team member…",
  ariaLabel,
  autoFocus,
  popoverClassName,
}: PersonPickerProps) {
  const [open, setOpen] = React.useState(false);
  const { data: membersData, isLoading } = useApi(teamManage.members, {});
  const { data: meData } = useApi(auth.me, {});
  const members = membersData?.members ?? [];
  const meId = meData?.userId ?? null;

  const selected = React.useMemo(() => toList(value), [value]);
  const byId = React.useMemo(() => {
    const m = new Map<number, TeamMember>();
    for (const mem of members) m.set(mem.userId, mem);
    return m;
  }, [members]);

  const labelFor = React.useCallback(
    (urn: string): string => {
      if (urn === SOLO_SELF) return "Just me (solo)";
      const id = userIdFromUrn(urn);
      const mem = id == null ? undefined : byId.get(id);
      const name = mem?.displayName || mem?.handle || (id != null ? `User ${id}` : urn);
      return id != null && id === meId ? `${name} (You)` : name;
    },
    [byId, meId],
  );

  // Commit on the turn after a change so onChange's new value is in flight.
  const commit = React.useCallback(() => {
    if (onCommit) setTimeout(onCommit, 0);
  }, [onCommit]);

  const emit = (next: string[]) => {
    onChange(multi ? next : (next[0] ?? ""));
    commit();
  };

  const toggle = (urn: string) => {
    if (multi) {
      emit(selected.includes(urn) ? selected.filter((u) => u !== urn) : [...selected, urn]);
    } else {
      emit([urn]);
      setOpen(false);
    }
  };

  const remove = (urn: string) => emit(selected.filter((u) => u !== urn));

  const triggerLabel =
    selected.length === 0
      ? placeholder
      : multi
        ? `${selected.length} selected`
        : labelFor(selected[0]!);

  return (
    <div className="flex flex-col gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            disabled={disabled}
            autoFocus={autoFocus}
            aria-label={ariaLabel}
            className={cn(
              "w-full justify-between font-normal",
              selected.length === 0 && "text-muted-foreground",
            )}
          >
            <span className="truncate">{triggerLabel}</span>
            <ChevronDown strokeWidth={1.5} className="size-4 shrink-0 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className={cn("w-[var(--radix-popover-trigger-width)] p-0", popoverClassName)}
        >
          <Command>
            <CommandInput placeholder="Search team members…" />
            <CommandList>
              <CommandGroup>
                <CommandItem value="just me solo myself" onSelect={() => toggle(SOLO_SELF)}>
                  <Check
                    strokeWidth={1.5}
                    className={cn(
                      "size-4 shrink-0",
                      selected.includes(SOLO_SELF) ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span>Just me (solo)</span>
                </CommandItem>
              </CommandGroup>
              <CommandEmpty>No match.</CommandEmpty>
              {!isLoading && members.length === 0 ? (
                <p className="px-3 py-2 type-subtext text-muted-foreground">
                  No registered teammates yet. Invite your team in Settings → Team.
                </p>
              ) : null}
              <CommandGroup>
                {members.map((mem) => {
                  const urn = userUrn(mem.userId);
                  const isSelected = selected.includes(urn);
                  return (
                    <CommandItem
                      key={mem.userId}
                      value={`${mem.displayName} ${mem.handle}`}
                      onSelect={() => toggle(urn)}
                    >
                      <Check
                        strokeWidth={1.5}
                        className={cn("size-4 shrink-0", isSelected ? "opacity-100" : "opacity-0")}
                      />
                      <span className="truncate">
                        {mem.displayName || mem.handle}
                        {mem.userId === meId ? (
                          <span className="text-muted-foreground"> (You)</span>
                        ) : null}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {multi && selected.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((urn) => (
            <Badge key={urn} variant="secondary" className="gap-1 pr-1">
              <span className="truncate">{labelFor(urn)}</span>
              {!disabled ? (
                <button
                  type="button"
                  aria-label={`Remove ${labelFor(urn)}`}
                  onClick={() => remove(urn)}
                  className="rounded-full p-0.5 hover:bg-foreground/10"
                >
                  <X strokeWidth={1.5} className="size-3" />
                </button>
              ) : null}
            </Badge>
          ))}
        </div>
      ) : null}
    </div>
  );
}
